/**
 * Every word the product shows for Submission Pack preparation — the pill, the
 * filters, the status menu, the counts, the header roll-up and the checklist
 * export all read from here. Change the wording in this file and nowhere else.
 *
 * What the state MEANS (R0-B, 2026-10-07): preparation progress. The wire
 * values stay `not_ready | in_progress | ready | not_required` for stored rows
 * and API clients, but `ready` only says the item is drafted or linked: every
 * linked section is draft complete, in review or approved; a form or
 * attachment has something linked; a schedule's sheets hold content; or an
 * acknowledgement is ticked. Nothing opens the file, checks it against the
 * tender or reads an approval, so no label here may say "ready", "approved"
 * or "ready to submit". Wording approved by Kapil 2026-10-07.
 *
 * The state read is `readiness` — the EFFECTIVE verdict (a manual status wins,
 * otherwise the server-derived one).
 *
 * `not_ready` reads neutral "To do" (Kapil, option B, 2026-09-23; "Not ready"
 * retired as a pill word, D3 of the status-words plan). One exception: a
 * blocking class (response / form / attachment) with nothing linked is a hole
 * in the pack, and turns `destructive` / "Missing" once the deadline is within
 * `MISSING_WITHIN_DAYS` days — and stays red once the deadline has passed.
 * No deadline means no countdown, so it stays "To do". On day one of a bid
 * most rows are empty; painting them all red said nothing useful. An
 * acknowledgement nobody has ticked yet, or a linked item still being worked
 * on, is never "Missing".
 */

import type { BadgeProps } from '@/components/ui/badge'
import type { SubmissionStatus, TriageClass } from '@/lib/api'
import { daysUntilDeadline } from '@/lib/bid-deadline'

/**
 * An empty blocking item turns from "To do" to "Missing" when the deadline is
 * this many calendar days away or fewer (past deadlines included).
 */
export const MISSING_WITHIN_DAYS = 3

/** The word for each state — pill, filter segment and status menu alike. */
export const PACK_STATUS_LABELS: Record<SubmissionStatus, string> = {
  ready: 'Prepared',
  in_progress: 'In progress',
  not_ready: 'To do',
  not_required: 'Not required',
}

/** An empty blocking item near (or past) the deadline. */
export const PACK_MISSING_LABEL = 'Missing'

/** Counts, captions and notes that describe preparation across the pack. */
export const PACK_PREPARATION_COPY = {
  /** "10 prepared" — the fold summary and the checklist's stats line. */
  preparedCount: (count: number): string => `${count} prepared`,
  /** The word after the bold "N of M" in the pack header. */
  headerSuffix: 'prepared',
  progressAriaLabel: (percent: number): string => `Pack preparation: ${percent}% prepared`,
  /** The Overview tile's value. */
  overviewTileValue: (prepared: number, total: number): string => `${prepared}/${total} prepared`,
  /** Under the header roll-up. */
  meaningNote: "Prepared means drafted or linked. It doesn't mean reviewed or approved.",
  /** The blockers band when nothing is flagged. */
  allClear: 'Nothing left to prepare.',
  /** Export dialog blocker line for not_ready forms and attachments. */
  handoversNotPrepared: (count: number): string =>
    `${count} ${count === 1 ? 'form or attachment' : 'forms and attachments'} not prepared`,
  /** Export dialog, above the open blockers. */
  exportBlockersNote:
    'The pack will still export. Anything not yet prepared is listed as missing in MANIFEST.xlsx, so the gaps travel with the zip.',
} as const

export interface PackReadinessLabel {
  label: string
  variant: BadgeProps['variant']
}

/** Classes whose emptiness blocks submission. `acknowledge` is the exception. */
function isBlockingClass(triageClass: TriageClass | null): boolean {
  return (triageClass ?? 'attachment') !== 'acknowledge'
}

export function packReadinessLabel(
  readiness: SubmissionStatus,
  /** null folds into 'attachment' — the same fallback the grouping uses. */
  triageClass: TriageClass | null,
  hasLink: boolean,
  /** The pack's submission deadline. `null` never escalates to "Missing". */
  deadline: string | null,
  now: Date = new Date()
): PackReadinessLabel {
  switch (readiness) {
    case 'ready':
      return { label: PACK_STATUS_LABELS.ready, variant: 'success' }
    case 'in_progress':
      return { label: PACK_STATUS_LABELS.in_progress, variant: 'warning' }
    case 'not_required':
      return { label: PACK_STATUS_LABELS.not_required, variant: 'outline' }
    case 'not_ready':
    default: {
      if (!isBlockingClass(triageClass) || hasLink) {
        return { label: PACK_STATUS_LABELS.not_ready, variant: 'secondary' }
      }
      const days = daysUntilDeadline(deadline, now)
      return days !== null && days <= MISSING_WITHIN_DAYS
        ? { label: PACK_MISSING_LABEL, variant: 'destructive' }
        : { label: PACK_STATUS_LABELS.not_ready, variant: 'secondary' }
    }
  }
}
